import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function DeleteConfirmModal({
  open,
  cursorX = 0,
  cursorY = 0,
  title,
  whenLabel,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation(['planning', 'common']);
  const panelRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ left: cursorX, top: cursorY, ready: false });

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos({ left: cursorX, top: cursorY, ready: false });
      return;
    }

    const panel = panelRef.current;
    const btn = confirmBtnRef.current;
    if (!panel || !btn) return;

    // Place at cursor first so we can measure, then offset so Delete button center = cursor
    panel.style.left = `${cursorX}px`;
    panel.style.top = `${cursorY}px`;

    const panelRect = panel.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const btnCenterX = btnRect.left + btnRect.width / 2 - panelRect.left;
    const btnCenterY = btnRect.top + btnRect.height / 2 - panelRect.top;

    let left = cursorX - btnCenterX;
    let top = cursorY - btnCenterY;

    if (left < 8) left = 8;
    if (top < 8) top = 8;
    if (left + panelRect.width > window.innerWidth - 8) {
      left = window.innerWidth - panelRect.width - 8;
    }
    if (top + panelRect.height > window.innerHeight - 8) {
      top = window.innerHeight - panelRect.height - 8;
    }

    setPanelPos({ left, top, ready: true });
    btn.focus();
  }, [open, cursorX, cursorY]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/40"
      style={{ backdropFilter: 'blur(4px)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div
        ref={panelRef}
        className="fixed w-[280px] bg-surface rounded-2xl shadow-[0_16px_40px_rgba(15,23,42,0.22)] border border-border overflow-hidden"
        style={{
          left: panelPos.left,
          top: panelPos.top,
          visibility: panelPos.ready ? 'visible' : 'hidden',
        }}
        role="alertdialog"
        aria-labelledby="delete-confirm-heading"
        aria-describedby="delete-confirm-desc"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-100 dark:border-red-800/60 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-[22px]">delete</span>
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <p id="delete-confirm-heading" className="text-sm font-bold text-text-main leading-tight">
                {t('deleteThisEvent')}
              </p>
              <p id="delete-confirm-desc" className="text-[11px] text-text-secondary mt-1 leading-snug">
                {t('cannotUndo')}
              </p>
            </div>
          </div>
          <div className="mt-3 rounded-xl bg-background-light border border-border px-3 py-2.5">
            <p className="text-sm font-semibold text-text-main truncate">{title}</p>
            <p className="text-[11px] text-text-secondary mt-0.5 truncate">{whenLabel}</p>
          </div>
        </div>
        <div className="px-3 pb-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="w-full px-3 py-2.5 text-xs font-semibold text-text-secondary hover:text-text-main bg-background-light hover:bg-slate-200/70 dark:hover:bg-slate-600/50 border border-border rounded-xl transition-colors"
          >
            {t('common:cancel')}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-[0_4px_12px_rgba(220,38,38,0.35)] active:scale-[0.98] transition-all"
          >
            <span className="material-symbols-outlined text-[16px]">delete</span>
            {t('deleteEvent')}
          </button>
        </div>
      </div>
    </div>
  );
}
